import { type FormEvent, useState } from 'react';
import type { RetornoLinha, TipoTroca, TipoTrocaNovo } from '@pcp/contracts';
import { useGravarLote, useTiposTroca, isOk } from '../../api/cadastros';
import { type Column, EditableTable } from '../../components/EditableTable';
import { ErrorState, LoadingBlock } from '../../components/states';
import { t } from '../../i18n/pt-BR';
import { num } from './shared';

const c = t.cadastros.types;
const MODES = [
  { value: 1, label: c.mode1 },
  { value: 2, label: c.mode2 },
  { value: 3, label: c.mode3 },
];

const EMPTY: TipoTrocaNovo = {
  'tipo-troca': '',
  descricao: '',
  'origem-valor': '',
  modo: 2,
  acumula: true,
  ativo: true,
};

export function TiposTrocaTab({ canEdit }: { canEdit: boolean }) {
  const query = useTiposTroca();
  const lote = useGravarLote('tipo-troca');
  const [draft, setDraft] = useState<TipoTrocaNovo>(EMPTY);
  const [result, setResult] = useState<RetornoLinha | null>(null);

  const columns: Column<TipoTroca>[] = [
    { key: 'tipo-troca', header: c.code, read: (r) => <strong>{r['tipo-troca']}</strong> },
    {
      key: 'descricao',
      header: c.description,
      read: (r) => r.descricao,
      edit: { kind: 'text', field: 'descricao', width: 220 },
    },
    {
      key: 'origem-valor',
      header: c.source,
      read: (r) => <span className="mono">{r['origem-valor']}</span>,
      edit: { kind: 'text', field: 'origem-valor', width: 120 },
    },
    {
      key: 'modo',
      header: c.mode,
      read: (r) => MODES.find((m) => m.value === r.modo)?.label ?? r.modo,
      edit: { kind: 'select', field: 'modo', options: MODES, numeric: true },
    },
    {
      key: 'tolerancia',
      header: c.tolerance,
      numeric: true,
      read: (r) => num(r.tolerancia),
      edit: { kind: 'number', field: 'tolerancia', step: 0.01, min: 0 },
    },
    {
      key: 'acumula',
      header: c.accumulates,
      read: (r) => (r.acumula ? t.cadastros.yes : t.cadastros.no),
      edit: { kind: 'check', field: 'acumula' },
    },
    {
      key: 'ativo',
      header: c.active,
      read: (r) => (r.ativo ? t.cadastros.yes : t.cadastros.no),
      edit: { kind: 'check', field: 'ativo' },
    },
  ];

  const save = async (row: TipoTroca, changes: Partial<TipoTroca>) => {
    const [r] = await lote.mutateAsync({ metodo: 'PUT', linhas: [{ ...row, ...changes }] });
    return r!;
  };
  const remove = async (row: TipoTroca) => {
    const [r] = await lote.mutateAsync({ metodo: 'DELETE', linhas: [row] });
    return r!;
  };
  const create = async (e: FormEvent) => {
    e.preventDefault();
    const code = draft['tipo-troca'].trim().toUpperCase();
    const [r] = await lote.mutateAsync({ metodo: 'POST', linhas: [{ ...draft, 'tipo-troca': code }] });
    setResult(r!);
    if (r && isOk(r)) setDraft(EMPTY);
  };

  return (
    <section className="panel" aria-labelledby="tab-types">
      <h2 id="tab-types">{t.cadastros.tabs.changeoverTypes}</h2>
      <p className="hint">{c.hint}</p>
      {query.isLoading ? <LoadingBlock rows={4} /> : null}
      {query.error ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.data ? (
        <EditableTable
          rows={query.data}
          rowKey={(r) => r['tipo-troca']}
          rowLabel={(r) => r['tipo-troca']}
          columns={columns}
          canEdit={canEdit}
          onSave={save}
          onDelete={remove}
          onReload={() => void query.refetch()}
        />
      ) : null}

      {canEdit ? (
        <form onSubmit={(e) => void create(e)} className="new-row" aria-label={c.newTitle}>
          <strong>{c.newTitle}</strong>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="nt-code">{c.code}</label>
              <input
                id="nt-code"
                value={draft['tipo-troca']}
                onChange={(e) => setDraft((d) => ({ ...d, 'tipo-troca': e.target.value }))}
              />
            </div>
            <div className="field">
              <label htmlFor="nt-desc">{c.description}</label>
              <input
                id="nt-desc"
                value={draft.descricao}
                onChange={(e) => setDraft((d) => ({ ...d, descricao: e.target.value }))}
              />
            </div>
            <div className="field">
              <label htmlFor="nt-src">{c.source}</label>
              <input
                id="nt-src"
                value={draft['origem-valor']}
                onChange={(e) => setDraft((d) => ({ ...d, 'origem-valor': e.target.value }))}
              />
            </div>
            <div className="field">
              <label htmlFor="nt-mode">{c.mode}</label>
              <select
                id="nt-mode"
                className="select"
                value={draft.modo}
                onChange={(e) => setDraft((d) => ({ ...d, modo: Number(e.target.value) as 1 | 2 | 3 }))}
              >
                {MODES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="field" style={{ alignSelf: 'end' }}>
              <button
                type="submit"
                className="btn"
                disabled={!draft['tipo-troca'].trim() || !draft.descricao.trim() || lote.isPending}
              >
                {t.cadastros.add}
              </button>
            </div>
          </div>
          {result && !isOk(result) ? (
            <div className="form-error" role="alert">
              {result.errordescription}
            </div>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}
